/**
 * fetchConfig
 * @description Default options that can be used in fetch requests.
 * @param {String} type
 * @returns {Object} default fetch config options
 */
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

/**
 * CartRemoveButton
 * @description Removes the line item based on index. Web component needs to be inside the cart items web component.
 */
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      // Update quantity if the button is enabled (it's disabled if it's a free product)
      if(this.disabled) return;
      this.closest('cart-items').updateQuantity(this.dataset.index, 0);
    });
  }
}

if(!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}

/**
 * CartItems
 * @description Handles the cart drawer and cart interaction.
 */
class CartItems extends HTMLElement {

  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);
    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.isGiftWrapInCart = false;
    this.cartHasGiftWrapDirectlyAttribute = false;

    // Look for changes in the cart items web component
    this.addEventListener('change', this.debouncedOnChange.bind(this));
    this.handleGiftWrap();
    this.handleGiftWrapDirectlyAttribute();

    // Make sure all values are refreshed when going back to a cached page for example
    setTimeout(() => {
      this.refreshInterface();
    }, 100);

    window.onpageshow = (event) => {
      setTimeout(() => {
        this.refreshInterface();
      }, 100);
    };
  }

  /**
   * onChange
   * @param {Object} event
   */
   onChange(event) {
    const item = event.target.closest('[data-item]');
    const variantId = item?.dataset.itemVariantId;
     // Check if the target has the line index data-index attribute
     if(event.target.dataset.index) {
      this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), variantId);
    }
  }

  /**
   * handleGiftWrap
   * @description Set the click event on the giftWrapButton.
   */
  handleGiftWrap () {
    this.giftWrapButton = this.querySelector('[data-gift-wrap="add"]');
    this.giftWrapProductVariantId = parseInt(this.giftWrapButton?.dataset?.variantId);

    // Remove any existing event listeners by cloning the button
    if (this.giftWrapButton) {
      const newButton = this.giftWrapButton.cloneNode(true);
      this.giftWrapButton.parentNode.replaceChild(newButton, this.giftWrapButton);
      this.giftWrapButton = newButton;

      this.giftWrapButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.addGiftWrapToCart();
      });
    }
  }

  /**
   * addGiftWrapToCart
   * @description Add the gift wrap product to the cart.
   */
  addGiftWrapToCart() {

    // Disable the button
    this.giftWrapButton.setAttribute('disabled', true);

    // Set loading state in interface
    this.enableLoading();

    // Get values
    const variantId = parseInt(this.giftWrapButton.dataset?.variantId);
    const quantity = parseInt(this.giftWrapButton.dataset?.quantity);

    if (variantId && quantity) {

      const body = {
        items: [
          {
            id: variantId,
            quantity: quantity,
            properties: {
              _giftwrap: 'true',
            }
          }
        ]
      };

      this.postToCart(routes.cart_add_url, JSON.stringify(body))
        .then(()=>{
          return this.addFreeProductToCart();
        })
        .then(()=>{
          return this.refreshInterface();
        })
        .then(() => {
          this.disableLoading();
          this.giftWrapButton.removeAttribute('disabled');
        });
    }
  }

  /**
   * handleGiftWrapDirectlyAttribute
   * @description Set the click event on the [data-gift-wrap="checkbox"] element.
   */
  handleGiftWrapDirectlyAttribute() {
    this.giftWrapCheckbox = this.querySelector('[data-gift-wrap="checkbox"]');

    if(this.giftWrapCheckbox) {
      this.cartHasGiftWrapDirectlyAttribute = this.giftWrapCheckbox.checked;

      // Remove any existing event listeners by cloning the checkbox
      const newCheckbox = this.giftWrapCheckbox.cloneNode(true);
      this.giftWrapCheckbox.parentNode.replaceChild(newCheckbox, this.giftWrapCheckbox);
      this.giftWrapCheckbox = newCheckbox;

      this.giftWrapCheckbox.addEventListener('change', (event) => {
        event.preventDefault();
        this.enableLoading();
        this.giftWrapCheckbox.setAttribute('disabled', 'true');
        this.updateGiftWrapDirectlyAttribute(this.giftWrapCheckbox.checked)
          .then(()=>{
            // update url returns no cart, so fetch it...
            return fetch(`${routes.cart_url}.json`)
              .then((response)=>{
                return response.json();
              })
              .then((cart)=>{
                return this.updateGiftWrapProductInCart(cart);
              })
          })
          .then(()=>{
            return this.refreshInterface();
          })
          .then(()=>{
            this.disableLoading();
            this.giftWrapCheckbox.removeAttribute('disabled');
          });
      });
    }
  }

  /**
   * updateGiftWrapDirectlyAttribute
   * @description Sets the correct '_giftwrap_directly' attribute in the cart, used to send directly to the customer
   * @param {Boolean} attributeValue
   * @returns {Promise}
   */
  updateGiftWrapDirectlyAttribute (attributeValue) {
    // Check: if value not true, change into empty for removing attribute from cart
    let value = attributeValue.toString();
    if (value !== 'true') {
      value = '';
    }

    const body = {
      attributes: {
        '_giftwrap_directly': value
      }
    };

    // Do the post
    return this.postToCart(routes.cart_update_url, JSON.stringify(body))
      .then(() => {
        this.cartHasGiftWrapDirectlyAttribute = attributeValue;
      });

  }

  /**
   * addFreeProductToCart
   * @description Add the free product to the cart if its set
   */
  addFreeProductToCart() {
    this.freeProduct = document.querySelector('[data-free-gift]');

    // Return when there is no freeProduct set or found.
    if(!this.freeProduct) {
      return;
    }

    this.freeProductVariantId = parseInt(this.freeProduct?.dataset.freeGiftId);

    const body = {
      items: [
        {
          id: this.freeProductVariantId,
          quantity: 1,
          properties: {
            _promotion: 'gift',
          }
        }
      ],
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    };

    return this.postToCart(routes.cart_add_url, JSON.stringify(body))
      .then((response) => {
        return response;
      })
    }


  /**
   * getSectionsToRender
   * @returns {Array}
   */
  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-cart-item-contents',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-cart-footer-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '[data-cart-icon-bubble]',
      },
      {
        id: 'cart-drawer-icon-bubble',
        section: 'cart-drawer-icon-bubble',
        selector: '[data-cart-drawer-icon-bubble]',
      }
    ];
  }

  /**
   * updateQuantity
   * @description Handles the updates the product quantity changes in the cart items list.
   * @param {Number} line
   * @param {Number} quantity
   * @param {String} name
   */
  updateQuantity(line, quantity, name, variantId) {

    this.enableLoading(line);

    // Check if we have the gift wrap product is being removed from the cart
    let giftWrapProductIsBeingRemoved = false;
    if(variantId && this.giftWrapButton) {
      if(variantId == this.giftWrapButton.dataset?.variantId && quantity == 0) {
        // remove the attribute to from the cart
        giftWrapProductIsBeingRemoved = true;
      }
    }

    // Build object to post to the cart
    const body = {
      line,
      quantity
    };

    this.postToCart(routes.cart_change_url, JSON.stringify(body), line, quantity, name)
      .then((cart) => {
        // Now check if we have the cart attribute in the cart, because we need to remove it if the quantity is 0
        if(this.cartHasGiftWrapDirectlyAttribute) {
          return this.updateGiftWrapProductInCart(cart);
        }
      })
      .then(() => {
        if(giftWrapProductIsBeingRemoved) {
          return this.updateGiftWrapDirectlyAttribute(false);
        }
      })
      .then(()=>{
        return this.addFreeProductToCart();
      })
      .then(()=>{
        return this.refreshInterface();
      })
      .finally(()=>{
        this.disableLoading();
      });

  }

  /**
   * updateGiftWrapProductInCart
   * @description Calculates and posts the correct quantity for the gift wrap product.
   * @param {Object} cart
   * @returns {Promise}
   */
  updateGiftWrapProductInCart(cart) {
    if(!cart) {
      return;
    }

    // Set defaults
    let line;
    let newGiftWrapProductQuantity = 0;
    let freeProductVariantId = +(this.freeProductVariantId || document.querySelector('[data-free-gift]')?.dataset.freeGiftId);

    // Count total products minus gift wrap product and set the line index of the gift wrap product
    cart.items.forEach((item, index) => {
      if(item.variant_id != this.giftWrapProductVariantId && item.variant_id != freeProductVariantId) {
        newGiftWrapProductQuantity = newGiftWrapProductQuantity + item.quantity;
      }
      if(item.variant_id == this.giftWrapProductVariantId) {
        line = index + 1;
      }
    });

    if(newGiftWrapProductQuantity > 0 && line) {
      let body = {
        line,
        quantity: newGiftWrapProductQuantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      }
      return this.postToCart(routes.cart_change_url, JSON.stringify(body), line, newGiftWrapProductQuantity);
    }
  }

  /**
   * postToCart
   * @description Used for all the posts to the cart endpoint.
   * @param {String} url, one of the cart api endpoints
   * @param {String} body
   * @returns {Promise} with the parsedState object
   */
  postToCart(url, body, line, name) {
    return fetch(url, {...fetchConfig(), ...{ body }})
      .then((response) => {
        return response.text();
      })
      .then((response) => {
        const parsedState = JSON.parse(response);
        return parsedState;
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  }


  /**
   * refreshInterface
   * @description Update the interface with a section api request to multiple sections to render
   * @param {Object} parsedState
   */
  refreshInterface() {

    // Collect sections that we will use for the section api render, don't forget it has a limit of 5.
    let sections = this.getSectionsToRender().map((section) => section.section).join(',');

    return fetch(`${routes.cart_url}?sections=${sections}`)
      .then(response => response.json())
      .then(data => {
        let cart;
        this.getSectionsToRender().forEach((section => {
          if (section.id == 'main-cart-items') {
            const parser = new DOMParser;
            const content = parser.parseFromString(data[section.section], 'text/html');
            cart = JSON.parse(content.querySelector('[data-cart-json]').innerHTML);
          }
          const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
          // check if the elementToReplace / section exists (the cart drawer icon bubble doesn't exist in the cart page e.g)
          if (elementToReplace) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(data[section.section], section.selector);
          }
        }));
        // Re-bind the giftwrap add button
        this.handleGiftWrap();
        this.handleGiftWrapDirectlyAttribute();
        this.classList.toggle(data['cart-drawer'] ? 'cart-drawer-is-empty' : 'is-empty', cart.item_count === 0);
        document.getElementById('main-cart-footer')?.classList.toggle('is-empty', cart.item_count === 0);
      });
  }

  /**
   * updateLiveRegions
   * @description Show error texts, should not be deleted.
   * @param {Number} line
   * @param {Number} itemCount
   */
  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      document.getElementById(`Line-item-error-${line}`)
        .querySelector('.cart-item__error-text')
        .innerHTML = window.cartStrings.quantityError.replace(
          '[quantity]',
          document.getElementById(`Quantity-${line}`).value
        );
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  /**
   * getSectionInnerHTML
   * @description Parse HTML from string to DOM.
   * @param {String} html
   * @param {String} selector
   */
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  /**
   * enableLoading
   * @description Toggles the loading state in the interface.
   */
  enableLoading() {
    document.getElementById('main-cart-items').classList.add('cart__items--disabled');
    document.querySelector('.loading-overlay').classList.remove('hidden');
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  /**
   * disableLoading
   * @description Removes the loading state in the interface.
   */
  disableLoading() {
    document.querySelector('.loading-overlay').classList.add('hidden');
    document.getElementById('main-cart-items').classList.remove('cart__items--disabled');
    // Re-bind events
    this.handleGiftWrap();
    this.handleGiftWrapDirectlyAttribute();
  }

}

if(!customElements.get('cart-items')){
  customElements.define('cart-items', CartItems);
}
